import React, { useState, useRef, useCallback, useEffect } from "react";

/**
 * InfiniteScrollList
 *
 * Props:
 * - fetchItems: async (pageNumber, pageSize) => { data: [], totalCount: number }
 * - RenderItem: React component, receives { item, ...itemProps }
 * - itemProps: object (optional, extra props passed to RenderItem)
 * - pageSize: number (default 10)
  const loadingRef = useRef(false);
 * - initialPage: number (default 1)
 * - loader: ReactNode (optional, shown while loading)
 * - noMore: ReactNode (optional, shown when no more items)
 */
function InfiniteScrollList({
  fetchItems,
  RenderItem,
  itemProps = {},
  pageSize = 10,
  initialPage = 1,
  loader = <div>Loading...</div>,
  noMore = <div>No more items.</div>,
  allItems = []
}) {
  const loadingRef = useRef(false);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(initialPage);
  const pageNumberRef = useRef(initialPage);
  const [totalCount, setTotalCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const loaderRef = useRef();

  const hasMore = totalCount === null || items.length < totalCount;

  // Fetch next page
  // Always use the latest pageNumber from the ref
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;
    loadingRef.current = true;
    setLoading(true);
    setError("");
    try {
      const res = await fetchItems(pageNumberRef.current, pageSize); 
      if (!res || !Array.isArray(res.Items)) {
        console.log("Invalid response from fetchItems:");
       setError("Failed to load items");
       return;
      } 
      
      setItems(prev => [...prev, ...(Array.isArray(res.Items) ? res.Items : [])]);
      setTotalCount(res.TotalCount);
      setPageNumber(pn => {
        const next = pn + 1;
        pageNumberRef.current = next;
        return next;
      });
    } catch (err) {
      setError(err.message || "Failed to load items");
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [fetchItems, pageSize, hasMore]);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!hasMore) return;
    const observer = new window.IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root: null, rootMargin: "200px", threshold: 0 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
  }, [loadMore, hasMore, items.length]);

  // Keep ref in sync with state
  useEffect(() => {
    pageNumberRef.current = pageNumber;
  }, [pageNumber]);

  // Initial load
  useEffect(() => {
    if (items.length === 0 && hasMore && !loading) {
      loadMore();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      {items.map((item, i) => (
    <RenderItem key={item._id || item.id || i} {...item} {...itemProps} allItems={allItems} />
      ))
      
      
      }
      {error && <div style={{ color: "red" }}>{error}</div>}
      <div ref={loaderRef}>
        {loading && loader}
        {/* {!hasMore && noMore} */}
      </div>
    </>
  );
}

export default InfiniteScrollList;